import { Alert, Box, FormField, FormHelp, Heading, Label, Paragraph } from '@zillow/constellation-10';

import { TimeZoneDropdownOptions } from '../../../../constants/TourCalendar';

import type { TimeZoneIds } from '../../../../constants/TourCalendar';
import TimeZoneDropdownSelect from '../../../TimeZoneDropdownSelect';
import type { ComboboxOptionObjectInterface, ComboboxOptionType } from '@zillow/constellation-10';
import TourDurationDropdownSelect from '../../../TourDurationDropdownSelect';
import type { TourDuration } from '@zg-rentals/ts-z-calendar-client';

type TourCalendarTimeZoneProps = {
  onTimeZoneSelection: (timeZone: TimeZoneIds) => void;
  onTourDurationSelection: (tourDuration: TourDuration) => void;
  tourDuration?: TourDuration;
  timeZone?: TimeZoneIds;
  disableTimeZoneSelection: boolean;
  canAccessPostingPathTourDuration?: boolean;
};

const TourCalendarTimeZone = ({
  onTimeZoneSelection,
  onTourDurationSelection,
  timeZone,
  tourDuration,
  disableTimeZoneSelection,
  canAccessPostingPathTourDuration = false,
}: TourCalendarTimeZoneProps) => {
  const handleOptionSelect = (option: ComboboxOptionType) => {
    const timeZoneId = TimeZoneDropdownOptions.find(
      (timeZoneOption) => timeZoneOption.value === (option as ComboboxOptionObjectInterface).value,
    )?.value;

    if (timeZoneId) {
      onTimeZoneSelection(timeZoneId);
    }
  };

  const handleTourDurationSelect = (option: ComboboxOptionType) => {
    const tourDurationValue = (option as ComboboxOptionObjectInterface).value as TourDuration;
    if (tourDurationValue) {
      onTourDurationSelection(tourDurationValue);
    }
  };

  const gridColumnStart = { base: '2', md: '4' };
  const gridColumnEnd = { base: 'span 22', md: 'span 10' };

  return (
    <>
      <Box
        css={{ gridColumnStart, gridColumnEnd, marginTop: { md: 'tighter' } }}
        data-testid="TourCalendarTimeZone-Body"
      >
        {canAccessPostingPathTourDuration ? (
          <Heading level={4} css={{ marginBottom: 'looser' }}>
            Tour Settings
          </Heading>
        ) : (
          <>
            <Heading level={4} fontFamily="heading-alt" css={{ marginBottom: 'tighter' }}>
              Select the property time zone
            </Heading>
            <Paragraph color="text.subtle" css={{ marginBottom: { base: 'loose', md: 40 } }}>
              This helps ensure that your tour availability matches what’s provided to interested renters.
            </Paragraph>
          </>
        )}
      </Box>
      <Box css={{ gridColumnStart, gridColumnEnd }}>
        {canAccessPostingPathTourDuration ? (
          <>
            <FormField
              label={<Label size="lg">Tour duration</Label>}
              control={
                <TourDurationDropdownSelect tourDuration={tourDuration} handleOptionSelect={handleTourDurationSelect} />
              }
              required={true}
              css={{ marginBottom: 'loose' }}
            />
            <FormField
              label={<Label size="lg">Property time zone</Label>}
              description={
                <FormHelp>
                  This helps ensure that your tour availability matches what’s provided to interested renters.
                </FormHelp>
              }
              control={
                <TimeZoneDropdownSelect
                  timeZone={timeZone}
                  handleOptionSelect={handleOptionSelect}
                  disableTimeZoneSelection={disableTimeZoneSelection}
                />
              }
            />
          </>
        ) : (
          <FormField
            label={<Label size="lg">Property time zone</Label>}
            control={
              <TimeZoneDropdownSelect
                timeZone={timeZone}
                handleOptionSelect={handleOptionSelect}
                disableTimeZoneSelection={disableTimeZoneSelection}
              />
            }
          />
        )}
      </Box>
      <Box css={{ gridColumnStart, gridColumnEnd, marginTop: 'loose' }}>
        <Alert data-testid="TourCalendarTimeZone-Alert" tone="warning">
          The time zone cannot be edited after continuing to the next page. Check that it&apos;s correct before
          proceeding.
        </Alert>
      </Box>
    </>
  );
};

export default TourCalendarTimeZone;
